export function newClauseId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function normalizeClauseRows(rows) {
  return (rows || [])
    .map((row, i) => ({
      id: row?.id || newClauseId(),
      article_no: Number.isFinite(Number(row?.article_no)) ? Number(row.article_no) : i + 1,
      title: row?.title || "",
      content: row?.content || "",
      order: Number.isFinite(Number(row?.order)) ? Number(row.order) : i,
      enabled: row?.enabled !== false,
      created_at: row?.created_at,
      updated_at: row?.updated_at,
    }))
    .sort((a, b) => a.order - b.order)
    .map((row, i) => ({ ...row, order: i }));
}

export function normalizeSpecialRows(rows) {
  return (rows || [])
    .map((row, i) => {
      if (typeof row === "string") {
        return { id: newClauseId(), text: row, order: i, enabled: true };
      }
      return {
        id: row?.id || newClauseId(),
        text: row?.text || "",
        order: Number.isFinite(Number(row?.order)) ? Number(row.order) : i,
        enabled: row?.enabled !== false,
      };
    })
    .sort((a, b) => a.order - b.order)
    .map((row, i) => ({ ...row, order: i }));
}

export function ContractSpecialEditor({ rows, onChange, withEnabled = false, addLabel = "특약 추가" }) {
  const setRows = (next) => onChange(normalizeSpecialRows(next));
  const update = (i, patch) => setRows(rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };
  const add = () => setRows([...rows, { id: newClauseId(), text: "", enabled: true }]);

  return (
    <div className="space-y-2" data-testid="contract-special-editor">
      {rows.map((row, i) => (
        <div key={row.id || i} className="flex gap-2 items-start">
          <span className="text-[10px] text-white/30 w-5 pt-2">{i + 1}</span>
          <textarea
            value={row.text}
            onChange={(e) => update(i, { text: e.target.value })}
            rows={2}
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs"
          />
          <div className="flex flex-col gap-1 shrink-0">
            <button type="button" onClick={() => move(i, -1)} className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50">위</button>
            <button type="button" onClick={() => move(i, 1)} className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50">아래</button>
            {withEnabled ? (
              <button
                type="button"
                onClick={() => update(i, { enabled: row.enabled === false })}
                className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50"
              >
                {row.enabled === false ? "비활성" : "활성"}
              </button>
            ) : null}
            <button type="button" onClick={() => remove(i)} className="border border-red-500/20 text-red-300 rounded px-2 py-0.5 text-[10px]">삭제</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs border border-white/15 rounded px-3 py-1.5">{addLabel}</button>
    </div>
  );
}

export default function ContractClauseEditor({
  rows,
  onChange,
  withEnabled = false,
  addLabel = "조항 추가",
}) {
  const setRows = (next) => onChange(normalizeClauseRows(next));
  const update = (i, patch) => setRows(rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };
  const add = () => {
    const maxNo = rows.reduce((n, row) => Math.max(n, Number(row.article_no) || 0), 0);
    setRows([...rows, { id: newClauseId(), article_no: maxNo + 1, title: "", content: "", enabled: true }]);
  };
  const insertAfter = (i) => {
    const maxNo = rows.reduce((n, row) => Math.max(n, Number(row.article_no) || 0), 0);
    const next = [...rows];
    next.splice(i + 1, 0, { id: newClauseId(), article_no: maxNo + 1, title: "", content: "", enabled: true });
    setRows(next);
  };

  return (
    <div className="space-y-3" data-testid="contract-clause-editor">
      {rows.map((row, i) => (
        <div key={row.id || i} className={`rounded-xl border p-3 space-y-2 ${row.enabled === false ? "border-white/5 opacity-60" : "border-white/10"}`}>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[10px] text-white/30 w-6">#{i + 1}</span>
            <label className="text-[10px] text-white/40">조</label>
            <input
              type="number"
              min="1"
              value={row.article_no}
              onChange={(e) => update(i, { article_no: Number(e.target.value) || 1 })}
              className="w-16 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1 text-xs"
            />
            <input
              placeholder="조항 제목"
              value={row.title}
              onChange={(e) => update(i, { title: e.target.value })}
              className="flex-1 min-w-[140px] bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1 text-xs"
            />
            <div className="flex flex-wrap gap-1">
              <button type="button" onClick={() => move(i, -1)} className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50">위</button>
              <button type="button" onClick={() => move(i, 1)} className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50">아래</button>
              <button type="button" onClick={() => insertAfter(i)} className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50">아래에 삽입</button>
              {withEnabled ? (
                <button
                  type="button"
                  onClick={() => update(i, { enabled: row.enabled === false })}
                  className="border border-white/10 rounded px-2 py-0.5 text-[10px] text-white/50"
                >
                  {row.enabled === false ? "비활성" : "활성"}
                </button>
              ) : null}
              <button type="button" onClick={() => remove(i)} className="border border-red-500/20 text-red-300 rounded px-2 py-0.5 text-[10px]">삭제</button>
            </div>
          </div>
          <textarea
            placeholder="조항 본문 (줄바꿈으로 문단 구분, - 로 시작하는 줄은 불릿)"
            value={row.content}
            onChange={(e) => update(i, { content: e.target.value })}
            rows={5}
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs leading-relaxed"
          />
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs border border-white/15 rounded px-3 py-1.5">{addLabel}</button>
    </div>
  );
}
